import Add16 from '@carbon/icons-react/es/add/16';
import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import { Tabs } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TaskType } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Collapse, Panel } from 'components/atoms/collapse';
import { ShowForVendor } from 'components/atoms/user-type-utils';
import {
  AttachmentsTabViews,
  TaskAttachments,
} from 'components/molecules/tasks/details/attachments';
import { TaskCsat } from 'components/molecules/tasks/details/csat';
import { DependentTasks } from 'components/molecules/tasks/details/dependent-tasks';
import { TaskDescription } from 'components/molecules/tasks/details/description';
import { PrivateNote } from 'components/molecules/tasks/details/private-note';
import { TaskFields } from 'components/molecules/tasks/details/task-fields';
import { TimeEntrySetting } from 'components/molecules/tasks/details/time-entry-settings';
import { SectionPanelKeyHash } from 'components/molecules/tasks/details/types';
import {
  StyledTabs,
  TabPaneScrollWrapper,
} from 'components/organisms/task-pane/task-detail/styled';
import { TaskTabKeys } from 'components/organisms/task-pane/task-detail/task-detail';
import { VariablesContext } from 'contexts/variables';
import { TemplateObjectType } from 'data/templates/api';
import { useTemplateMentionsData } from 'data/templates/hooks';
import {
  ProjectTemplateTask,
  useProjectTemplateTask,
} from 'data/templates/projects';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useIsNativeUser } from 'hooks/use-user-type';
import {
  AttachmentsSectionPanel,
  Card as StyledCard,
  DependentTaskSection,
  TaskFieldsSection,
  TemplateTaskHeader,
} from '../../../../commons';
import { TaskMoreActions, useTemplateTaskCommonActions } from '../../commons';
import { TaskDetailBreadcrumb } from './breadcrumb';
import { ProjectPlanFormDetails } from './form';

const activeCollapseKeys = Object.values(SectionPanelKeyHash);

const Card = styled(StyledCard)`
  min-height: 50vh;
  width: 688px;
  .ant-card-body {
    padding: var(--spacing-04) 0;
  }
`;
const { TabPane } = Tabs;

const ProjectTemplateTaskDetails: FC<{
  task: ProjectTemplateTask;
  taskId: number;
}> = ({ task, taskId }) => {
  const { members, spaceTabs, tasks } = useTemplateMentionsData({
    forPrivateContext: task?.private,
  });
  const isNativeUser = useIsNativeUser();
  const headerRef = useRef<HTMLDivElement>(null);

  const [showPrivateNoteEditor, setPrivateNoteEditorVisibility] =
    useState<boolean>(!!task.privateTaskDescription?.length);

  const [selectedTab, setSelectedTab] = useState(
    task.formTemplateId ? TaskTabKeys.Form : TaskTabKeys.BasicDetails,
  );

  const [view, setView] = useState(AttachmentsTabViews.GridView);

  useEffect(() => {
    queueMicrotask(() =>
      headerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      }),
    );
  }, [taskId]);

  useEffect(() => {
    if (task.formTemplateId) {
      setSelectedTab(TaskTabKeys.Form);
    } else {
      setSelectedTab(TaskTabKeys.BasicDetails);
    }
    setPrivateNoteEditorVisibility(!!task.privateTaskDescription?.length);
  }, [task.formTemplateId, task.privateTaskDescription]);

  const {
    onUpdateDescription,
    onUpdatePrivateNote,
    onUpdateTask,
    onUpdateName,
    onUpdateAssignee,
    onCreateDependency,
    onUpdateCSATState,
    onAddMention,
    onChangeResponsibility,
  } = useTemplateTaskCommonActions(task);

  const openPrivatePanel = () => {
    setPrivateNoteEditorVisibility(true);
  };

  const [timesheetsEnabled] = useAccountFeature(AccountFeature.Timesheets);

  return (
    <Card
      bordered={false}
      private={task.private}
      type="inner"
      title={
        <div ref={headerRef}>
          <TaskDetailBreadcrumb taskId={task.taskId} />
          <TemplateTaskHeader
            moreActions={<TaskMoreActions task={task} />}
            task={task}
            onUpdateName={onUpdateName}
            onUpdateAssignee={onUpdateAssignee}
            onChangeResponsibility={onChangeResponsibility}
          />
        </div>
      }
    >
      <StyledTabs
        tabBarGutter={1}
        activeKey={selectedTab}
        onChange={(value) => setSelectedTab(value)}
      >
        {task.formTemplateId ? (
          <TabPane tab={<FormattedMessage id="form" />} key={TaskTabKeys.Form}>
            <VariablesContext.Provider
              value={{
                allowSimpleVariables: true,
              }}
            >
              <ProjectPlanFormDetails formTemplateId={task.formTemplateId} />
            </VariablesContext.Provider>
          </TabPane>
        ) : null}
        <TabPane
          tab={<FormattedMessage id="basic_details" />}
          key={TaskTabKeys.BasicDetails}
        >
          <TabPaneScrollWrapper>
            <Collapse
              defaultActiveKey={activeCollapseKeys}
              expandIconPosition="right"
              expandIcon={({ isActive }) => {
                return isActive ? <CaretDown16 /> : <CaretUp16 />;
              }}
            >
              <Panel
                key={SectionPanelKeyHash.Description}
                header={<TaskDescription.Title />}
              >
                <TaskDescription.Editor
                  taskDescription={task.taskDescription}
                  mentionData={{
                    members,
                    spaceTabs,
                    tasks: {
                      items: Object.values(tasks.items).filter(
                        (t) => t.taskId !== taskId,
                      ),
                    },
                  }}
                  updateTask={onUpdateDescription}
                  onMentionAdd={(data) =>
                    onAddMention(data, { isContextPrivate: task.private })
                  }
                  templateType={TemplateObjectType.Project}
                />
                <ShowForVendor>
                  {!showPrivateNoteEditor ? (
                    <Button
                      type="link"
                      size="small"
                      icon={<Add16 />}
                      onClick={openPrivatePanel}
                    >
                      <FormattedMessage id="task.private_note.add" />
                    </Button>
                  ) : null}
                </ShowForVendor>
              </Panel>
              {showPrivateNoteEditor && isNativeUser ? (
                <Panel
                  key={SectionPanelKeyHash.PrivateNote}
                  header={<PrivateNote.Title />}
                >
                  <PrivateNote.Editor
                    mentionData={{
                      members,
                      spaceTabs,
                      tasks: {
                        items: Object.values(tasks.items).filter(
                          (t) => t.taskId !== taskId,
                        ),
                      },
                    }}
                    note={task.privateTaskDescription}
                    onUpdate={onUpdatePrivateNote}
                    onMentionAdd={(data) =>
                      onAddMention(data, { isContextPrivate: true })
                    }
                  />
                </Panel>
              ) : null}
              <Panel
                key={SectionPanelKeyHash.Attachments}
                header={<TaskAttachments.Title />}
                extra={
                  Boolean(task.attachments?.length) && (
                    <TaskAttachments.Tabs onChooseTab={setView} />
                  )
                }
              >
                <AttachmentsSectionPanel
                  task={task}
                  view={view}
                  onUpdateTask={onUpdateTask}
                />
              </Panel>
            </Collapse>
          </TabPaneScrollWrapper>
        </TabPane>
        <TabPane
          tab={<FormattedMessage id="additional_details" />}
          key={TaskTabKeys.AdditionalDetails}
        >
          <TabPaneScrollWrapper>
            <Collapse
              defaultActiveKey={activeCollapseKeys}
              expandIconPosition="right"
              expandIcon={({ isActive }) => {
                return isActive ? <CaretDown16 /> : <CaretUp16 />;
              }}
            >
              <Panel
                key={SectionPanelKeyHash.TaskFields}
                header={<TaskFields.Title />}
              >
                <TaskFieldsSection task={task} onUpdateTask={onUpdateTask} />
              </Panel>
              {timesheetsEnabled ? (
                <Panel
                  key={SectionPanelKeyHash.TimeEntrySetting}
                  header={<TimeEntrySetting.Title />}
                >
                  <TimeEntrySetting.List
                    task={task}
                    onUpdateTask={onUpdateTask}
                  />
                </Panel>
              ) : null}
              <Panel
                id={SectionPanelKeyHash.DependentTasks}
                key={SectionPanelKeyHash.DependentTasks}
                header={<DependentTasks.Title />}
              >
                <DependentTaskSection
                  task={task}
                  onCreateDependency={onCreateDependency}
                  onUpdateTask={onUpdateTask}
                />
              </Panel>
              {task.type === TaskType.Milestone && !task.private && (
                <Panel
                  id={SectionPanelKeyHash.Csat}
                  key={SectionPanelKeyHash.Csat}
                  header={<TaskCsat.Title />}
                  extra={
                    <TaskCsat.Status
                      disabled={false}
                      checked={task.isCsatEnabled}
                      onChange={onUpdateCSATState}
                    />
                  }
                >
                  <TaskCsat.Detail
                    name={task.taskName}
                    isWaiting={false}
                    ratings={[]}
                  />
                </Panel>
              )}
            </Collapse>
          </TabPaneScrollWrapper>
        </TabPane>
      </StyledTabs>
    </Card>
  );
};

export const ProjectTemplateTaskDetailView: FC<{
  taskId: number;
}> = ({ taskId }) => {
  const task = useProjectTemplateTask(taskId);

  if (!task) {
    return null;
  }

  return <ProjectTemplateTaskDetails task={task} taskId={taskId} />;
};
